/**
 * Hook do Centro de Notificações Premium
 *
 * Encapsula a busca server-side filtrada (por módulo, status e texto),
 * gestão de seleção em lote, arquivamento e contagens por módulo.
 *
 * Diferente de `useAgentNotifications` (que cuida do badge/realtime/toasts
 * globais), este hook é usado dentro do Sheet do Centro de Notificações.
 *
 * @version 1.3.0 - 2026-08-07 — Cache do item aberto no detalhe: clicar em uma notificação
 *   no filtro "Não lidas" não fecha mais o painel (o item saía da lista após markRead).
 * @version 1.2.0 - 2026-07-24 — Após ações (markRead/markUnread/markAllRead/archive*),
 *   invalida os caches de `useAgentNotifications` para sincronizar o contador do
 *   sino no header imediatamente, sem depender de F5 ou do staleTime de 60s.
 * @version 1.0.0 - 2026-04-20
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { AgentNotification } from '@/hooks/useAgentNotifications';

const logger = createLogger('useNotificationCenter');

/**
 * Módulos disponíveis no Centro de Notificações.
 * Devem espelhar o mapeamento da RPC get_my_notifications.
 */
export type NotificationModule =
  | 'all'
  | 'conversations'
  | 'mentions'
  | 'activities'
  | 'scheduling'
  | 'contracts'
  | 'financial'
  | 'tasks'
  | 'crm';

export type NotificationStatusFilter = 'unread' | 'all' | 'archived';

/** Notificação retornada pela RPC já com archived_at. */
export interface NotificationCenterItem extends AgentNotification {
  archived_at: string | null;
}

interface UseNotificationCenterOptions {
  enabled?: boolean;
  limit?: number;
}

/**
 * Mapeia o tipo da notificação para um módulo agregado.
 * Mantém paridade com o WHERE da RPC get_my_notifications.
 */
export function mapTypeToModule(type: AgentNotification['type']): Exclude<NotificationModule, 'all'> {
  switch (type) {
    case 'transfer_received':
    case 'auto_assigned':
    case 'department_transfer':
      return 'conversations';
    case 'activity_mention':
    // v1.2.0: menções em mensagens privadas do chat também vão para "Menções"
    case 'chat_note_mention' as AgentNotification['type']:
      return 'mentions';
    case 'scheduling_booking':
      return 'scheduling';
    case 'contract_signed':
      return 'contracts';
    case 'payment_received':
      return 'financial';
    case 'task_assigned':
    case 'task_comment_added':
      return 'tasks';
    case 'deal_seller_assigned':
      return 'crm';
    // v1.1.0: cadência atrasada vai para a aba "Atividades"
    case 'cadence_step_overdue' as AgentNotification['type']:
      return 'activities';
    default:
      return 'conversations';
  }
}

export function useNotificationCenter(opts: UseNotificationCenterOptions = {}) {
  const { enabled = true, limit = 100 } = opts;
  const { user } = useAuth();
  const qc = useQueryClient();

  /**
   * Invalida os caches consumidos pelo NotificationBell (badge/lista globais),
   * mantidos por `useAgentNotifications`. Cobre `list` e `unread-count` de
   * qualquer `limit` através do prefixo comum `['agent-notifications']`.
   */
  const invalidateBellCaches = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['agent-notifications'] });
  }, [qc]);

  const [items, setItems] = useState<NotificationCenterItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [module, setModule] = useState<NotificationModule>('all');
  const [status, setStatus] = useState<NotificationStatusFilter>('unread');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [activeId, setActiveIdState] = useState<string | null>(null);

  /**
   * v1.3.0 — Cache do item aberto no painel de detalhe.
   *
   * Motivo: no filtro "Não lidas", clicar em uma notificação dispara markRead,
   * o refetch remove o item da lista e o detalhe ficava vazio. Mantendo uma
   * cópia local do item aberto, o detalhe continua visível mesmo quando o
   * registro sai do filtro corrente.
   */
  const [activeItemCache, setActiveItemCache] = useState<NotificationCenterItem | null>(null);


  /**
   * Busca notificações respeitando filtros atuais.
   * Para contar todos os módulos no header (pills), usamos uma busca
   * separada com p_module=NULL (ver fetchCounts abaixo).
   */
  const fetchItems = useCallback(async () => {
    if (!enabled || !user?.id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_my_notifications', {
        p_limit: limit,
        p_module: module === 'all' ? null : module,
        p_status: status,
        p_search: search.trim() || null,
        p_include_archived: status === 'archived',
      });

      if (error) {
        logger.error('Erro ao buscar notificações do centro:', error);
        return;
      }

      setItems(((data || []) as unknown as NotificationCenterItem[]));
    } finally {
      setLoading(false);
    }
  }, [enabled, user?.id, limit, module, status, search]);

  /**
   * Estado independente para contagens por módulo (usado pelas pills).
   * Sempre busca não lidas, sem filtro de módulo, para popular badges.
   */
  const [countsByModule, setCountsByModule] = useState<Record<NotificationModule, number>>({
    all: 0, conversations: 0, mentions: 0, activities: 0, scheduling: 0,
    contracts: 0, financial: 0, tasks: 0, crm: 0,
  });

  const fetchCounts = useCallback(async () => {
    if (!enabled || !user?.id) return;
    // Server-side: agregação por módulo via RPC dedicada (≤7 linhas).
    // Substitui o fetch antigo de até 200 notificações cruas + agregação no JS.
    const { data, error } = await supabase.rpc('get_my_notification_module_counts');
    if (error) {
      logger.error('Erro ao buscar contagens por módulo:', error);
      return;
    }
    const counts: Record<NotificationModule, number> = {
      all: 0, conversations: 0, mentions: 0, activities: 0, scheduling: 0,
      contracts: 0, financial: 0, tasks: 0, crm: 0,
    };
    (data || []).forEach((r: { module: string; unread_count: number }) => {
      if (r.module in counts) {
        counts[r.module as NotificationModule] = Number(r.unread_count) || 0;
      }
    });
    setCountsByModule(counts);
  }, [enabled, user?.id]);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  /** Marca itens como lidos via RPC existente. */
  const markRead = useCallback(async (ids: string[]) => {
    if (ids.length === 0) return;
    const { error } = await supabase.rpc('mark_notifications_as_read', {
      p_notification_ids: ids,
      p_mark_all: false,
    });
    if (error) {
      logger.error('Erro ao marcar como lida:', error);
      return;
    }
    // Update otimista para evitar flicker antes do refetch server-side.
    setItems(prev => prev.map(n => ids.includes(n.id) ? { ...n, is_read: true, read_at: new Date().toISOString() } : n));
    // v1.3.0: espelha no cache do detalhe (item pode sair do filtro "Não lidas").
    setActiveItemCache(prev =>
      prev && ids.includes(prev.id) ? { ...prev, is_read: true, read_at: new Date().toISOString() } : prev,
    );
    // Refetch garante consistência com o filtro atual (ex.: "Não lidas") + limpa seleção.
    await Promise.all([fetchItems(), fetchCounts()]);
    setSelectedIds(new Set());
    invalidateBellCaches();
  }, [fetchItems, fetchCounts, invalidateBellCaches]);

  /**
   * Marca itens selecionados como NÃO lidas (reverter).
   * Útil quando o usuário quer deixar uma notificação como lembrete visual.
   * v1.1.0 - 2026-07-24
   */
  const markUnread = useCallback(async (ids: string[]) => {
    if (ids.length === 0) return;
    const { error } = await supabase.rpc('mark_notifications_as_unread', {
      p_notification_ids: ids,
    });
    if (error) {
      logger.error('Erro ao marcar como não lida:', error);
      return;
    }
    setItems(prev => prev.map(n => ids.includes(n.id) ? { ...n, is_read: false, read_at: null } : n));
    // v1.3.0: espelha no cache do detalhe.
    setActiveItemCache(prev =>
      prev && ids.includes(prev.id) ? { ...prev, is_read: false, read_at: null } : prev,
    );
    await Promise.all([fetchItems(), fetchCounts()]);
    setSelectedIds(new Set());
    invalidateBellCaches();
  }, [fetchItems, fetchCounts, invalidateBellCaches]);

  /** Marca todas as notificações filtradas como lidas. */
  const markAllRead = useCallback(async () => {
    const { error } = await supabase.rpc('mark_notifications_as_read', {
      p_notification_ids: null,
      p_mark_all: true,
    });
    if (error) {
      logger.error('Erro ao marcar todas como lidas:', error);
      return;
    }
    setItems(prev => prev.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() })));
    await Promise.all([fetchItems(), fetchCounts()]);
    setSelectedIds(new Set());
    invalidateBellCaches();
  }, [fetchItems, fetchCounts, invalidateBellCaches]);


  /** Arquiva (toggle) uma seleção de notificações. */
  const archive = useCallback(async (ids: string[]) => {
    if (ids.length === 0) return;
    const { error } = await supabase.rpc('archive_notifications', {
      p_notification_ids: ids,
      p_archive_all: false,
      p_module: null,
    });
    if (error) {
      logger.error('Erro ao arquivar:', error);
      return;
    }
    await fetchItems();
    await fetchCounts();
    // v1.3.0: ao arquivar o item aberto, encerra o detalhe (nada a exibir).
    setActiveIdState(prev => (prev && ids.includes(prev) ? null : prev));
    setActiveItemCache(prev => (prev && ids.includes(prev.id) ? null : prev));
    setSelectedIds(new Set());
    invalidateBellCaches();
  }, [fetchItems, fetchCounts, invalidateBellCaches]);

  /** Arquiva todas notificações do filtro atual. */
  const archiveAllCurrent = useCallback(async () => {
    const { error } = await supabase.rpc('archive_notifications', {
      p_notification_ids: null,
      p_archive_all: true,
      p_module: module === 'all' ? null : module,
    });
    if (error) {
      logger.error('Erro ao arquivar todas:', error);
      return;
    }
    await fetchItems();
    await fetchCounts();
    setSelectedIds(new Set());
    invalidateBellCaches();
  }, [module, fetchItems, fetchCounts, invalidateBellCaches]);


  /** Seleção em lote */
  const toggleSelected = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);
  const selectAllVisible = useCallback(() => {
    setSelectedIds(new Set(items.map(i => i.id)));
  }, [items]);

  /**
   * v1.3.0 — Define o item ativo e memoriza o objeto correspondente.
   * Quando `id` é null, limpa também o cache do detalhe.
   */
  const setActiveId = useCallback((id: string | null) => {
    setActiveIdState(id);
    if (id === null) {
      setActiveItemCache(null);
    } else {
      setActiveItemCache(prev =>
        prev?.id === id ? prev : (items.find(i => i.id === id) || null),
      );
    }
  }, [items]);

  /**
   * v1.3.0 — Mantém o cache sincronizado sempre que o item ativo ainda
   * estiver presente na lista (ex.: mudou is_read / archived_at).
   */
  useEffect(() => {
    if (!activeId) return;
    const found = items.find(i => i.id === activeId);
    if (found) setActiveItemCache(found);
  }, [items, activeId]);

  // v1.3.0: usa o item da lista quando existe; senão, o cache (item saiu do filtro).
  const activeItem = useMemo(
    () =>
      items.find(i => i.id === activeId) ||
      (activeItemCache && activeItemCache.id === activeId ? activeItemCache : null),
    [items, activeId, activeItemCache],
  );


  return {
    // dados
    items,
    activeItem,
    loading,
    countsByModule,
    // filtros
    module, setModule,
    status, setStatus,
    search, setSearch,
    // seleção
    selectedIds,
    toggleSelected,
    clearSelection,
    selectAllVisible,
    // navegação
    activeId, setActiveId,
    // ações
    refetch: fetchItems,
    markRead,
    markUnread,
    markAllRead,

    archive,
    archiveAllCurrent,
  };
}
